import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, X } from 'lucide-react';
import api from '@/lib/api';

/**
 * "Convert to expense" dialog opened from the Banking Hub ledger's
 * inline "+ Expense" action on outgoing bank-payment rows.
 *
 * iter527 — Vendor field upgraded from free-text to a searchable
 * supplier combobox that hits /api/suppliers/names?q= which now
 * matches name OR account_number OR IBAN. Falls back to free-text
 * for ad-hoc vendors that aren't suppliers.
 */
export default function ConvertToExpenseDialog({
  row,
  onOpenChange,
  category,
  setCategory,
  vendor,
  setVendor,
  notes,
  setNotes,
  expenseCategories,
  saving,
  onSubmit,
  formatAmount,
  // iter527 — optional: parent can read the picked supplier id
  supplierId,
  setSupplierId,
}) {
  // Searchable supplier state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Reset on dialog close
  useEffect(() => {
    if (!row) {
      setQuery('');
      setResults([]);
      setOpen(false);
    }
  }, [row]);

  // Debounced server search by name OR account_number OR IBAN
  useEffect(() => {
    if (!row) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (query || '').trim();
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/suppliers/names?q=${encodeURIComponent(q)}&limit=12`);
        setResults(r.data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, row]);

  const pickSupplier = (s) => {
    setVendor(s.name || '');
    if (setSupplierId) setSupplierId(s.id || '');
    setQuery(s.name || '');
    setOpen(false);
  };

  const clearSupplier = () => {
    if (setSupplierId) setSupplierId('');
    setVendor('');
    setQuery('');
    setResults([]);
  };

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="hub-convert-dialog">
        <DialogHeader>
          <DialogTitle>Convert to expense</DialogTitle>
        </DialogHeader>
        {row && (
          <div className="space-y-3 text-xs">
            <div className="rounded-lg bg-muted/50 p-2.5 leading-relaxed">
              <div>
                <strong>{row.party_name}</strong> ·{' '}
                <span className="tabular-nums">{formatAmount(row.amount)}</span>
              </div>
              <div className="text-muted-foreground">
                {row.bank_account_name || 'No bank'} ·{' '}
                {row.ts ? new Date(row.ts).toLocaleDateString() : '—'}
              </div>
            </div>

            <div>
              <Label className="text-xs">Category *</Label>
              {expenseCategories.length > 0 ? (
                <>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="h-9" data-testid="hub-convert-category-select">
                      <SelectValue placeholder="Choose a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((c) => (
                        <SelectItem key={c.id || c.name} value={c.name || c.id}>
                          {c.name || c.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Or type a new one below — it will be saved as-is on the expense.
                  </p>
                </>
              ) : null}
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Telecom, Fuel, Rent…"
                className="mt-1"
                data-testid="hub-convert-category-input"
              />
            </div>

            {/* iter527 — Supplier search combobox (name OR account OR IBAN).
                User can still type a free-text vendor that's not a supplier. */}
            <div>
              <Label className="text-xs">Supplier / Vendor</Label>
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={query || vendor}
                  onChange={(e) => { setQuery(e.target.value); setVendor(e.target.value); if (setSupplierId) setSupplierId(''); }}
                  onFocus={() => { if (results.length > 0) setOpen(true); }}
                  placeholder="Search supplier by name, account # or IBAN…"
                  className="pl-7 pr-7"
                  data-testid="hub-convert-vendor"
                />
                {(query || vendor) && (
                  <button
                    type="button"
                    onClick={clearSupplier}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="hub-convert-vendor-clear"
                    aria-label="Clear"
                  >
                    <X size={12} />
                  </button>
                )}
                {open && results.length > 0 && (
                  <div className="absolute z-50 mt-1 left-0 right-0 bg-popover border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="hub-convert-vendor-results">
                    {results.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => pickSupplier(s)}
                        className="block w-full text-left px-3 py-1.5 hover:bg-muted/60 border-b last:border-b-0"
                        data-testid={`hub-convert-vendor-pick-${s.id}`}
                      >
                        <div className="font-medium">{s.name}</div>
                        {(s.account_number || s.iban) && (
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {s.account_number ? `Acc ${s.account_number}` : ''}
                            {s.account_number && s.iban ? ' · ' : ''}
                            {s.iban ? `IBAN ${s.iban}` : ''}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {supplierId
                  ? <span className="text-emerald-600">✓ Linked to existing supplier — expense will appear on their ledger.</span>
                  : (loading ? 'Searching…' : 'Pick from suppliers or just type a free-text vendor name.')}
              </p>
            </div>

            <div>
              <Label className="text-xs">Notes / description</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="hub-convert-notes"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="hub-convert-submit"
          >
            {saving ? 'Saving…' : 'Create expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
